import { IAccOpening } from '../src/components';
import { EBankType, ECompanyType, IAgentMergeInfo, ICompanyMergeInfo } from '../types/common';
import { ELianLianStepKey, ILianDetail } from '../types/lianlian';
import { getFileExt } from '@core/tools';
import { IStepItem } from '@vue3/components';

export function getAccOpeningSaveParam(d: IAccOpening) {
  const licenseUrl = d.bankAccountOpenLicenseUrl || '';
  /** 手动填写支行信息 */
  const customWriteBranchBank = d.customStatus === 1;

  return {
    lianId: Number(d.lianId),
    lianNo: d.lianNo,

    bankType: d.bankType || undefined,

    bankAccountName: d.bankAccountName,
    bankAccountOpenLicenseUrl: licenseUrl,
    bankAccountOpenLicenseType: getFileExt(licenseUrl),
    phoneNo: d.phoneNo,
    bankNo: d.bankNo,
    bankCode: d.bankCode,
    provinceCode: d.provinceCode,
    cityCode: d.cityCode,
    brabankName: d.brabankName,
    address: d.address,
    creditCode: d.creditCode,

    /** 对公账户开户行号/大额行号 */
    cnapsCode: customWriteBranchBank ? d.cnapsCode : d.bankBranchNo,
    /** 开户支行名称 */
    bankBranch: customWriteBranchBank ? d.customBankBranchName : d.relationBranchBankName,
    /** 支行信息是否自定义(0:否,1:是) */
    customStatus: d.customStatus || 0,

    contactEmail: d.contactEmail,
    contactName: d.contactName,
    contactPhone: d.contactPhone,
  };
}

export function getMergeLianInfo(agent: IAgentMergeInfo, detail = {} as ILianDetail): ILianDetail {
  const license = agent.agentBusiness;
  const legal = agent.agentLegal;
  const contact = agent.agentContacts;
  const acc = agent.agentLicense;

  detail.uboList = detail.uboList || [];

  if (license) {
    detail.businessLicensePic = license?.businessLicensePic;
    detail.name = license?.name;
    detail.address = license?.address;
    detail.creditCode = license?.creditCode;
    detail.legalName = license?.legalName;
    detail.businessLicenseStartDate = license?.businessLicenseStartDate;
    detail.businessLicenseEndDate = license?.businessLicenseEndDate;
  }

  if (legal) {
    detail.idNoFrontUrl = legal?.idNoFrontUrl;
    detail.idNoBackUrl = legal?.idNoBackUrl;
    detail.legalName = legal?.legalName;
    detail.legalPhone = legal?.legalPhone;
    detail.legalIdNo = legal?.legalIdNo;
    detail.idNoExpiryStartDate = legal?.idNoExpiryStartDate;
    detail.idNoExpiryEndDate = legal?.idNoExpiryEndDate;
  }

  if (acc) {
    detail.bankAccountOpenLicenseUrl = acc?.licenseUrl;
    detail.bankAccountName = acc?.accountName;
    detail.creditCode = acc?.creditCode;
    detail.bankNo = acc?.bankAccount;
    detail.address = acc?.address;
    detail.phoneNo = acc?.phoneNo;
  }

  if (contact) {
    detail.contactName = detail.contactName || contact?.contactName;
    detail.contactPhone = detail.contactPhone || contact?.contactPhone;
    detail.contactEmail = detail.contactEmail || contact?.contactEmail;
  }

  return getMergeOpenedAccountInfo(detail);
}

export function getMergePayerInfo(agent: ICompanyMergeInfo, detail = {} as ILianDetail): ILianDetail {
  const license = agent.companyBusiness;
  const legal = agent.companyLegal;
  const contact = agent.companyContacts;
  const acc = agent.companyLicense;

  detail.uboList = detail.uboList || [];

  if (license) {
    detail.businessLicensePic = license?.businessLicensePic;
    detail.name = license?.name;
    detail.address = license?.address;
    detail.creditCode = license?.creditCode;
    detail.legalName = license?.legalName;
    detail.businessLicenseStartDate = license?.businessLicenseStartDate;
    detail.businessLicenseEndDate = license?.businessLicenseEndDate;
    detail.businessScope = license?.businessScope;
  }

  if (legal) {
    detail.idNoFrontUrl = legal?.idNoFrontUrl;
    detail.idNoBackUrl = legal?.idNoBackUrl;
    detail.legalName = legal?.legalName;
    detail.legalPhone = legal?.legalPhone;
    detail.legalIdNo = legal?.legalIdNo;
    detail.idNoExpiryStartDate = legal?.idNoExpiryStartDate;
    detail.idNoExpiryEndDate = legal?.idNoExpiryEndDate;
  }

  if (contact) {
    detail.contactName = detail.contactName || contact?.contactName;
    detail.contactPhone = detail.contactPhone || contact?.contactPhone;
    detail.contactEmail = detail.contactEmail || contact?.contactEmail;
  }

  if (acc) {
    const bankType = acc.licenseType === 0 ? EBankType.LEGALREPT : acc.licenseType === 1 ? EBankType.ENTERPRISE : undefined;
    const isCompany = bankType === EBankType.ENTERPRISE;
    const isLegal = bankType === EBankType.LEGALREPT;

    detail.bankType = bankType;

    detail.bankAccountOpenLicenseUrl = isCompany ? acc.licenseUrl : isLegal ? acc.bankCardUrl : undefined;
    detail.bankNo = isCompany ? acc.bankAccount : isLegal ? acc.personBankAccount : undefined;
    detail.phoneNo = isCompany ? acc.phoneNo : isLegal ? acc.bankPhone : undefined;
    detail.bankAccountName = acc.accountName;
    detail.creditCode = acc.creditCode;
    detail.address = acc.address;
  }

  return getMergeOpenedAccountInfo(detail);
}

export function getMergeOpenedAccountInfo(d: ILianDetail) {
  const detail = JSON.parse(JSON.stringify(d)) as ILianDetail;
  const lianbank = detail.lianBank || {};

  const { bankBranchNo, relationBranchBankName, customBankBranchName } = detail;

  const {
    bankCode, provinceCode, customStatus, cityCode,
    cnapsCode, bankBranch, bankNo, bankAccountName,
  } = lianbank || {};

  detail.bankAccountName = bankAccountName || detail.bankAccountName;
  detail.bankNo = bankNo || detail.bankNo;
  detail.bankCode = bankCode;
  detail.provinceCode = provinceCode;
  detail.cityCode = cityCode;
  detail.customStatus = customStatus || 0;

  if (detail.customStatus === 0) { // 自动匹配支行信息
    detail.cnapsCode = '';
    detail.customBankBranchName = '';
    detail.bankBranchNo = bankBranchNo || cnapsCode;
    detail.relationBranchBankName = relationBranchBankName || bankBranch;
  } else {
    detail.bankBranchNo = '';
    detail.relationBranchBankName = '';
    detail.cnapsCode = cnapsCode;
    detail.customBankBranchName = customBankBranchName || bankBranch;
  }

  return detail;
}

/**
 * 获取分步steps
 * @param type {@link ECompanyType}
 * @description 公共函数，很多页面公用，如果要根据场景进行修改，请增加参数适配
 */
export function getLianlianSteps(type?: ECompanyType): IStepItem<ELianLianStepKey>[] {
  const isCompany = type === ECompanyType.Company;

  return [
    {
      title: '上传营业执照',
      key: ELianLianStepKey.License,
    },
    {
      title: isCompany ? '上传法人信息' : '上传经营者信息',
      key: ELianLianStepKey.Legal,
    },
    {
      title: '开户绑卡信息',
      key: ELianLianStepKey.AccOpening,
    },
    isCompany
      ? {
        title: '添加受益人',
        key: ELianLianStepKey.Beneficiary,
      }
      : null,
    {
      title: '支付信息认证',
      key: ELianLianStepKey.Submit,
    },
  ].filter(Boolean);
}

export function getPrevLianlianStep(current: ELianLianStepKey, type?: ECompanyType): IStepItem<ELianLianStepKey> | undefined {
  const steps = getLianlianSteps(type);
  const index = steps.findIndex((item) => item.key === current);

  return steps[index - 1];
}

export function getLianlianStep(current: ELianLianStepKey, type?: ECompanyType): IStepItem<ELianLianStepKey> | undefined {
  const steps = getLianlianSteps(type);

  return steps.find((item) => item.key === current);
}
